import { airlineById } from '@am/sim';
import type { Lang } from './i18n';

/*
 * Where the painted art is (public/art, see docs/art and its manifest): airline symbols,
 * passengers' faces, the game's logo, the billboards, the achievements' badges. Pictures load
 * lazily from the site; where one is missing, what was there before shows.
 */

/** Airlines with a symbol in art/airlines (ids 1 … this). */
const SYMBOLS = 25;

/** An airline's symbol: in its own colours, or white (on a tile or a tail in its colour). */
export function airlineSymbolUrl(id: number, white = false): string | null {
  const a = airlineById(id);
  if (!a || id < 1 || id > SYMBOLS) return null;
  const nn = String(id).padStart(2, '0');
  return `/art/airlines/airline-${nn}-${a.code.toLowerCase()}${white ? '-white' : ''}.png`;
}

/** Passengers' faces for the social feed (art/avatars), by mood. */
const FACES = {
  happy: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
  neutral: [17, 18, 19, 20, 21, 22, 23, 24],
  upset: [25, 26, 27, 28, 29, 30, 31, 32],
} as const;

/** A passenger's face in a mood: always the same one for the same person (`who`). */
export function passengerFaceUrl(mood: keyof typeof FACES, who: string): string {
  let h = 2166136261;
  for (let i = 0; i < who.length; i++) h = Math.imul(h ^ who.charCodeAt(i), 16777619);
  const faces = FACES[mood];
  const n = faces[(h >>> 0) % faces.length] ?? 1;
  return `/art/avatars/passenger-${String(n).padStart(2, '0')}.png`;
}

/** The game's logo, emblem and name side by side (the name is the same in every language). */
export const LOGO_URL = '/art/logo/logo-full.png';

/** The roadside billboards' ads (art/billboards/road-…), 2:1. */
export const ROAD_ADS = [
  '01-lale',
  '02-marti',
  '03-kapadokya',
  '04-ege',
  '05-kargo',
  '06-otel',
  '07-rentacar',
  '08-dutyfree',
  '09-kahve',
  '10-milkart',
  '11-metro',
  '12-karadeniz',
  '13-pist',
  '14-otopark',
] as const;
/** The totems on the terminal's kerb (art/billboards/totem-…), 3:5. */
export const TOTEMS = [
  '01-dutyfree',
  '02-kahve',
  '03-otel',
  '04-rentacar',
  '05-metro',
  '06-lale',
] as const;
/** The LED strips' messages (art/billboards/led-…), 8:1. */
export const LEDS = ['01-welcome', '02-goodflight', '03-taxi', '04-parking'] as const;

/** A poster in the game's language (painted in Turkish and English; the rest see English). */
export function posterUrl(kind: 'road' | 'totem' | 'led', slug: string, lang: Lang): string {
  return `/art/billboards/${kind}-${slug}-${lang === 'tr' ? 'tr' : 'en'}.webp`;
}

/** The wayfinding signs' pictograms (art/signs): white glyphs, no words. */
export const PICTOGRAMS = [
  'departures',
  'arrivals',
  'parking',
  'taxi',
  'bus',
  'train',
  'metro',
  'hotel',
  'car-rental',
  'cargo',
  'info',
  'first-aid',
  'fuel',
  'security',
  'baggage',
  'accessible',
] as const;
export type Pictogram = (typeof PICTOGRAMS)[number];

export function pictogramUrl(p: Pictogram): string {
  return `/art/signs/pict-${p}.png`;
}

/**
 * The far land and the far city round the world (art/horizon), by map (MapTheme): the green
 * hills, the desert's dunes and mesas, the mountains (scripts/horizon-art.py paints the last
 * two). They tile side by side.
 */
export const HORIZON_URLS: readonly (readonly [land: string, city: string])[] = [
  ['/art/horizon/horizon-hills.png', '/art/horizon/horizon-city.png'],
  ['/art/horizon/horizon-desert.png', '/art/horizon/horizon-desert-city.png'],
  ['/art/horizon/horizon-alpine.png', '/art/horizon/horizon-alpine-village.png'],
];

/** An account achievement's badge (art/achievements), by its id: firstFlight → ach-first-flight. */
export function achievementIconUrl(id: string): string {
  return `/art/achievements/ach-${id.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}.png`;
}

/** A rank's emblem (art/ranks), by the rank's id (see @am/protocol's RANKS). */
export function rankEmblemUrl(rank: string): string {
  return `/art/ranks/rank-${rank}.png`;
}

/** Loads a picture (null if it does not load). */
export function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}
